/**
 * Leftovers of older builds' service workers.
 *
 * 1. Old builds cached Google API responses (`google-apis`, NetworkFirst over
 *    googleapis.com and accounts.google.com). Those responses are
 *    authenticated and belong to one account (audit D9). The current service
 *    worker no longer has that route, so nothing ever expires or deletes them:
 *    they would stay in Cache Storage indefinitely.
 *
 * 2. `vite dev` runs without a service worker (vite.config.ts, devOptions),
 *    but one registered by an older dev setup stays. Its update check asks the
 *    dev server for /sw.js, gets index.html instead of a script, fails, and
 *    keeps the old worker: it serves a stale index.html (whose manifest link
 *    404s as "Manifest: Syntax error") and answers Google calls through its
 *    own cache.
 */

/** Caches written by older builds that must not survive. */
export const LEGACY_CACHES = ['google-apis'] as const

type CacheStore = Pick<CacheStorage, 'delete' | 'keys'>
type WorkerContainer = Pick<ServiceWorkerContainer, 'getRegistrations' | 'controller'>

/** Deletes the legacy caches that exist. Returns their names. */
export async function deleteLegacyCaches(cacheStorage: CacheStore | undefined = globalThis.caches): Promise<string[]> {
  if (!cacheStorage) return []
  const deleted: string[] = []
  for (const name of LEGACY_CACHES) {
    if (await cacheStorage.delete(name)) deleted.push(name)
  }
  return deleted
}

/**
 * Development only: unregisters every service worker on this origin, empties
 * Cache Storage, and reloads once if this page was still controlled by one.
 * Returns whether anything was removed. After the reload there is nothing to
 * remove, so it cannot loop.
 */
export async function removeDevServiceWorkers(
  container: WorkerContainer | undefined = globalThis.navigator?.serviceWorker,
  cacheStorage: CacheStore | undefined = globalThis.caches,
  reload: () => void = () => window.location.reload(),
): Promise<boolean> {
  if (!container) return false
  const registrations = await container.getRegistrations()
  if (registrations.length === 0) return false

  await Promise.all(registrations.map((r) => r.unregister()))
  if (cacheStorage) {
    for (const name of await cacheStorage.keys()) await cacheStorage.delete(name)
  }
  console.info(`[PWA] Removed ${registrations.length} development service worker(s) left by an older setup.`)
  if (container.controller) reload()
  return true
}
